import React, { useState } from 'react'
import { renderReact } from '@frontend/utils/react-compat'
import { Cautions } from '../../entites/FacilityCaution'
import {
  ContractStatusType,
  FacilityAvailableStatusCode,
  FacilityCategory,
  FacilityLunchType,
  FacilityMedicalInstitutionType,
  FacilityPhoneSupportText,
} from '../../entites/FacilityInfo'
import { FacilityOpeningTime } from '../../entites/FacilityOpeningTime'
import { ApiClient } from '../../utils/ApiClient'
import { convertToDateOrNull } from '../../utils/convertToDateOrNull'
import { ensureArray } from '../../utils/ensureArray'
import { generateId } from '../../utils/generateId'
import { getChildCategoryLabel } from '../../utils/getChildCategoryLabel'
import { getLunchTypeLabel } from '../../utils/getLunchTypeLabel'
import { getOpeningTimeText } from '../../utils/getOpeningTimeText'
import { BasicInformationTemplate } from './BasicInformationTemplate'
import { MainContainer } from './MainContainer'

document.addEventListener('DOMContentLoaded', () => {
  const dataElement = document.querySelector('#data')
  const dataProps = dataElement && dataElement.getAttribute('data-props')
  const props = dataProps === null ? null : (JSON.parse(dataProps) as Props)

  if (props === null) {
    throw `props passed by Rails is invalid: ${props}`
  }

  renderReact(
    <Container {...props} />,
    document.getElementById('react_container'),
  )
})

const Container = (props: Props) => {
  const [bookmarkId, setBookmarkId] = useState(props.bookmarkId)
  const handleBookmark = async () => {
    if (!props.id) return
    const url = `/facilities/${props.id}/family_facility_bookmarks`
    try {
      const res = await ApiClient.post(url)
      if (res.ok) {
        const data = await res.json()
        setBookmarkId(data.id)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const handleUnbookmark = async () => {
    if (!props.id || !bookmarkId) return
    const url = `/facilities/${props.id}/family_facility_bookmarks/${props.bookmarkId}`
    try {
      const res = await ApiClient.delete(url)
      // 404 はすでに削除済みなので削除成功として扱う
      if (res.ok || res.status === 404) {
        setBookmarkId(null)
      } else {
        // DTL-17912 参照
        const json = await res.json()
        if (json.message) {
          window.alert(json.message)
        }
      }
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <MainContainer>
      <BasicInformationTemplate
        isUserLoggedIn={props.isUserLoggedIn}
        isFacilityContracted={
          props.contractStatus === 'contracted' ? true : false
        }
        todayTelephoneSupportStartDate={convertToDateOrNull(
          props.todayTelephoneSupportStartDate,
        )}
        todayTelephoneSupportEndDate={convertToDateOrNull(
          props.todayTelephoneSupportEndDate,
        )}
        telephoneSupportText={props.telephoneSupportText}
        reservationFunctionStartDate={convertToDateOrNull(
          props.reservationFunctionStartDate,
        )}
        reservationFunctionEndDate={convertToDateOrNull(
          props.reservationFunctionEndDate,
        )}
        isRegistrationEnable={props.isRegistrationEnable}
        isFacilityVisualizalized={props.isFacilityVisualizalized}
        isBookmarked={!!bookmarkId}
        termUrl={
          props.hasTerms ? `/user_child_facilities/${props.id}/terms` : null
        }
        id={props.id}
        name={props.name}
        attachedClinic={
          props.attachedClinic
            ? {
                ...props.attachedClinic,
                siteUrl: props.attachedClinic.site_url,
              }
            : undefined
        }
        imageUrls={props.imageUrls.filter((_, i) => i < 3)}
        phoneNumber={props.phoneNumber}
        openingTimes={props.openingTimes.map((openingTime) =>
          getOpeningTimeText(openingTime, true),
        )}
        childCapacityCount={props.childCapacityCount}
        roomCount={props.roomCount}
        basicPrice={props.basicPrice}
        distance={props.distance}
        mapUrl={props.mapUrl}
        childCategory={getChildCategoryLabel(props.childCategory)}
        minAge={getMinAgeText(props.minAge)}
        maxAge={getMaxAgeText(props.maxAge)}
        availableAreas={
          props.availableAreas
            ? props.availableAreas.map((area) => area.name)
            : []
        }
        reservablePriorToRegistrationApproval={
          props.reservablePriorToRegistrationApproval ? 'できる' : 'できない'
        }
        lunchType={getLunchTypeLabel(props.lunchType)}
        medicalInstitutionType={props.medicalInstitutionType}
        cancelDeadline={props.cancelDeadline}
        cancelPrice={props.cancelPrice}
        childrenAvailable={props.childrenAvailable}
        childrenUnregistered={props.childrenUnregistered}
        todayStatusCode={props.todayStatusCode || 'not_contracted'}
        tomorrowStatusCode={props.tomorrowStatusCode || 'not_contracted'}
        todayStatusText={props.todayStatusText || ''}
        tomorrowStatusText={props.tomorrowStatusText || ''}
        description={props.description}
        collaborativeClinics={
          props.collaborativeClinics
            ? props.collaborativeClinics.map((clinic) => ({
                ...clinic,
                siteUrl: clinic.site_url,
              }))
            : []
        }
        unavailableDiseases={ensureArray(props.unavailableDiseases).map(
          (disease) => disease.name || '',
        )}
        unavailableDiseasesText={props.unavailableDiseasesText}
        options={
          props.options
            ? props.options.map((option) => ({
                ...option,
                note: option.note || undefined,
              }))
            : []
        }
        optionDescription={props.optionDescription}
        address={props.address}
        siteUrl={props.siteUrl}
        cautions={
          props.cautions
            ? props.cautions.filter((block) => block.id && block.body)
            : [
                {
                  id: generateId(),
                  body: '',
                  items: [],
                },
              ]
        }
        onUnbookmark={handleUnbookmark}
        onBookmark={handleBookmark}
        onBack={() => {
          window.history.back()
        }}
      />
    </MainContainer>
  )
}

type Props = {
  isUserLoggedIn: boolean
  isFacilityVisualizalized: boolean
  todayTelephoneSupportStartDate: string | null
  todayTelephoneSupportEndDate: string | null
  telephoneSupportText: FacilityPhoneSupportText | null
  reservationFunctionStartDate: string | null
  reservationFunctionEndDate: string | null
  isRegistrationEnable: boolean
  hasTerms: boolean
  bookmarkId: number | null
  contractStatus: ContractStatusType
  id: number
  name: string
  attachedClinic: ClinicResponse
  phoneNumber: string
  openingTimes: FacilityOpeningTime[]
  childCapacityCount: number
  roomCount: number
  basicPrice: string
  distance: number | null
  mapUrl: string
  childCategory: FacilityCategory
  minAge: any
  maxAge: any
  availableAreas: AvailableArea[] | null
  reservablePriorToRegistrationApproval: boolean
  lunchType: FacilityLunchType
  medicalInstitutionType: FacilityMedicalInstitutionType
  cancelDeadline: string
  cancelPrice: string
  todayStatusCode: FacilityAvailableStatusCode | null
  tomorrowStatusCode: FacilityAvailableStatusCode | null
  todayStatusText: string | null
  tomorrowStatusText: string | null
  description: string
  collaborativeClinics: ClinicResponse[] | null
  unavailableDiseases: UnavailableDisease[] | null
  unavailableDiseasesText: string
  options: Option[] | null
  optionDescription: string | null
  siteUrl: string
  cautions: Cautions | null
  address: string
  childrenAvailable: ChildAvailable[]
  childrenUnregistered: ChildUnregistered[]
  imageUrls: string[]
}

type ChildUnregistered = {
  name: string
  imageUrl: string | null
}

type ChildAvailable = ChildUnregistered & {
  status: 'available' | 'interview_scheduled'
}

export type ClinicResponse = {
  name: string
  site_url: string
}

type UnavailableDisease = {
  name: string
}

type Option = {
  title: string
  price: number
  note: string | null
}

type AvailableArea = {
  name: string
}

type MinAgeYearAndMonth = {
  type: 'year_and_month'
  year: number
  month: number
}
type MinAgeDay = {
  type: 'day'
  day: number
}
type MinAge = MinAgeYearAndMonth | MinAgeDay
export const getMinAgeText = (minAge: MinAge): string => {
  if (!minAge || !minAge.type) {
    return ''
  }
  const text =
    minAge.type === 'day'
      ? `生後${minAge.day}日`
      : `${minAge.year}歳${minAge.month}か月`
  return text
}

type MaxAgePreSchool = {
  type: 'pre_school'
}

type MaxAgeSchool = {
  type: 'school'
  grade: number
}

type MaxAge = MaxAgePreSchool | MaxAgeSchool

export const getMaxAgeText = (maxAge: MaxAge): string => {
  if (!maxAge || !maxAge.type) {
    return ''
  }
  const text =
    maxAge.type === 'pre_school' ? '未就学児' : `小学校${maxAge.grade}年生`
  return text
}
